import { BodyMedium, LinkButton, Modal, theme, CheckLightIcon } from '@arnold/common';
import { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { EmployeeRecord, TeamRecord, ValidationError } from './OrganizationImport';
import { ImportTab } from './constants';
import { filterEmptyEmployees, filterEmptyTeams } from './utils';

type IProps = {
  employees: EmployeeRecord[];
  teams: TeamRecord[];
  setSelectedTab: (tab: ImportTab) => void;
  scrollToCell: (row: number, column: number) => void;
  numberOfChangedEmployeeRecords: number;
  numberOfChangedTeamRecords: number;
  discardChanges: () => void;
  errors: ValidationError[];
  onSaveClicked: () => void;
  gridRef: React.RefObject<HTMLDivElement>;
  validationInProgress: boolean;
  finalStep?: boolean;
};

const Panel = styled.div<{ opened?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 320px;
  height: 100vh;
  background-color: white;
  padding: ${theme.spacing.h};
  display: flex;
  flex-direction: column;
  border-left: ${theme.spacing.a} solid #ddd;
`;

const InfoBox = styled.div`
  border: ${theme.spacing.a} solid ${theme.colors.borderMain.default};
  border-radius: ${theme.spacing.d};
  padding: ${theme.spacing.f};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.f};
  margin-bottom: ${theme.spacing.f};
`;

const InfoBoxWithErrors = styled(InfoBox)`
  max-height: calc(100% - 300px);
  overflow: auto;
  gap: ${theme.spacing.d};
`;

const StatusData = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.d};
`;

const NumberContainer = styled.span`
  font-size: ${theme.typography.body.large.bold.fontSize};
  line-height: ${theme.typography.body.large.bold.lineHeight};
  font-weight: ${theme.typography.body.large.bold.fontWeight};
`;
const Label = styled.span`
  font-size: ${theme.typography.body.small.regular.fontSize};
  line-height: ${theme.typography.body.small.regular.lineHeight};
  font-weight: ${theme.typography.body.small.regular.fontWeight};
  color: ${theme.colors.text.secondary};
`;

const SuccessInfoBox = styled.div`
  border: ${theme.spacing.a} solid ${theme.colors.emotionSuccess.default};
  border-radius: ${theme.spacing.d};
  padding: ${theme.spacing.f};
  background-color: ${theme.colors.emotionSuccess.background};
  display: flex;
  gap: ${theme.spacing.f};
`;

const StyledCheckLightIcon = styled(CheckLightIcon)`
  flex-shrink: 0;
  path {
    fill: ${theme.colors.emotionSuccess.default};
  }
`;

const ErrorInfoBox = styled.div<{ onClick?: () => void }>`
  border: ${theme.spacing.a} solid ${theme.colors.emotionDanger.default};
  border-radius: ${theme.spacing.d};
  padding: ${theme.spacing.f};
  background-color: ${theme.colors.emotionDanger.background};
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
`;

const ButtonsContainer = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
`;

const ButtonWithoutMargin = styled(Button)`
  margin: 0px !important;
`;

const StyledLinkButton = styled(LinkButton)`
  margin-top: ${theme.spacing.g};
  margin-bottom: 0;
  color: ${theme.colors.emotionDanger.default} !important;

  &:hover {
    color: ${theme.colors.emotionDanger.hover} !important;
  }
  &&:active,
  &&:focus {
    color: ${theme.colors.emotionDanger.active} !important;
  }
`;

const HelpLinkButton = styled(LinkButton)`
  display: inline-block;
  float: right;
`;

const HelpButtonContainer = styled.div`
  margin-bottom: ${theme.spacing.g};
`;

export const EmployeeValidationPanel = ({
  employees,
  teams,
  setSelectedTab,
  scrollToCell,
  numberOfChangedEmployeeRecords,
  numberOfChangedTeamRecords,
  discardChanges,
  errors,
  onSaveClicked,
  gridRef,
  validationInProgress,
  finalStep,
}: IProps) => {
  const { t } = useTranslation('organizationImportCard');
  const [validationSpinner, setValidationSpinner] = useState(false);

  const [unsavedChangesModalShow, setUnsavedChangesModalShow] = useState(false);
  const [unsavedChangesModalCallback, setUnsavedChangesModalCallback] = useState<(() => void) | null>(null);

  const onPrimaryButtonClick = () => {
    if (validationInProgress) {
      return setValidationSpinner(true);
    }
    if (errors.length === 0) {
      onSaveClicked();
    }
  };
  const onSecondaryButtonClick = () => {
    discardChanges();
    gridRef.current?.focus();
  };

  useEffect(() => {
    if (!validationInProgress) {
      setValidationSpinner(false);
      if (validationSpinner && errors.length === 0) {
        onSaveClicked();
      }
    }
  }, [errors.length, onSaveClicked, validationInProgress, validationSpinner]);

  useEffect(() => {
    const element = document.getElementById('admin-page-wrapper');
    if (element) {
      element.style.width = 'calc(100% - 320px)';
    }

    return () => {
      if (element) {
        element.style.width = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalStep]);

  const history = useHistory();
  useEffect(() => {
    if (finalStep) return;
    const unblock = history.block((tx) => {
      if (
        (numberOfChangedEmployeeRecords > 0 || numberOfChangedTeamRecords > 0) &&
        !tx.pathname.includes('/updateTeam')
      ) {
        setUnsavedChangesModalShow(true);
        setUnsavedChangesModalCallback(() => () => {
          unblock();
          history.push(tx);
        });

        return false;
      }
    });

    return () => {
      unblock();
    };
  }, [numberOfChangedEmployeeRecords, numberOfChangedTeamRecords, history, finalStep]);

  return (
    <>
      {createPortal(
        <>
          {
            <Panel>
              <HelpButtonContainer>
                <HelpLinkButton
                  onClick={() => {
                    window.open(
                      'https://intercom.help/arnold-robot/cs/articles/7826013-pridat-celou-strukturu-zamestnancu',
                      '_blank',
                    );
                  }}
                >
                  {t('help')}
                </HelpLinkButton>
              </HelpButtonContainer>
              <InfoBox>
                <div>
                  <StatusData>
                    <NumberContainer>{filterEmptyEmployees(employees).length}</NumberContainer>
                    <Label>
                      {t('employeesInOrganization', {
                        count: filterEmptyEmployees(employees).length,
                        postProcess: 'interval',
                      })}
                    </Label>
                  </StatusData>
                  <Label>
                    {t('numberOfChanged', { count: numberOfChangedEmployeeRecords, postProcess: 'interval' })}
                  </Label>
                </div>
                <div>
                  <StatusData>
                    <NumberContainer>{filterEmptyTeams(teams).length}</NumberContainer>
                    <Label>
                      {t('teamsInOrganization', { count: filterEmptyTeams(teams).length, postProcess: 'interval' })}
                    </Label>
                  </StatusData>
                  <Label>{t('numberOfChanged', { count: numberOfChangedTeamRecords, postProcess: 'interval' })}</Label>
                </div>
              </InfoBox>
              {errors.length === 0 && (
                <SuccessInfoBox>
                  <StyledCheckLightIcon width={22} height={22} color="red" />
                  <BodyMedium color={theme.colors.emotionSuccess.default}>{t('allEmployeesValid')}</BodyMedium>
                </SuccessInfoBox>
              )}
              {errors.length > 0 && (
                <InfoBoxWithErrors data-cy="error-info-box-container">
                  <ErrorInfoBox>
                    <BodyMedium color={theme.colors.emotionDanger.default} bold>
                      {t('foundErrors', { count: errors.length, postProcess: 'interval' })}
                    </BodyMedium>
                  </ErrorInfoBox>
                  {errors.map((error, index) => (
                    <ErrorInfoBox
                      data-cy="error-info-box"
                      data-error-message={error.message}
                      onClick={() => {
                        setSelectedTab(error.tab as ImportTab);
                        scrollToCell(error.position[0].column, error.position[0].row);
                      }}
                    >
                      <BodyMedium color={theme.colors.emotionDanger.default} bold>
                        {error.tab === ImportTab.EMPLOYEES
                          ? t('employeesRow', { row: [...new Set(error.position.map((p) => p.row + 1))].join(',') })
                          : t('teamsRow', { row: error.position.map((p) => p.row + 1).join(',') })}
                      </BodyMedium>
                      <BodyMedium color={theme.colors.emotionDanger.default}>
                        {t(error.message, error.messageObject)}
                      </BodyMedium>
                      <div>
                        <StyledLinkButton
                          variant="link"
                          onClick={() => {
                            setSelectedTab(error.tab as ImportTab);
                            scrollToCell(error.position[0].column, error.position[0].row);
                          }}
                          data-cy="error-button"
                        >
                          {t('showErrorButton')}
                        </StyledLinkButton>
                      </div>
                    </ErrorInfoBox>
                  ))}
                </InfoBoxWithErrors>
              )}
              <ButtonsContainer>
                <Button variant="outline-primary" onClick={onSecondaryButtonClick} data-cy="secondary-button">
                  {t('discardChangesButton')}
                </Button>
                <ButtonWithoutMargin onClick={onPrimaryButtonClick} data-cy="import-table-button">
                  {validationSpinner ? (
                    <>
                      <Spinner animation="border" size="sm" />
                    </>
                  ) : (
                    t('confirmChangesButton')
                  )}
                </ButtonWithoutMargin>
              </ButtonsContainer>
            </Panel>
          }
        </>,
        document.getElementById('root')!,
      )}

      <Modal
        show={unsavedChangesModalShow}
        onHide={() => {
          setUnsavedChangesModalShow(false);
          setUnsavedChangesModalCallback(null);
        }}
        onSubmit={() => {
          setUnsavedChangesModalShow(false);

          if (unsavedChangesModalCallback) {
            unsavedChangesModalCallback();
            setUnsavedChangesModalCallback(null);
          }
        }}
        title={t('unsavedChangesModal:Title')}
        text={t('unsavedChangesModal:Description')}
        buttons={{
          submit: { title: t('unsavedChangesModal:Cancel') },
          cancel: { title: t('unsavedChangesModal:Stay') },
        }}
      />
    </>
  );
};
